import { create } from 'zustand';
import type { Session } from '@supabase/supabase-js';
import type { Actor, Permission, UserRole } from '@vns/core';
import { supabase } from './supabase';
import { sessionStore } from '../platform';

/**
 * Who is signed in, and what they may do.
 *
 * The Actor built here drives what the UI offers. It is NOT a security boundary
 * — Row Level Security in the database decides what is actually possible. If a
 * screen ever shows something this says it should not, that is a UI bug; if the
 * database allows something it should not, that is a real one.
 */
interface SessionState {
  session: Session | null;
  actor: Actor | null;
  /** True until the first auth check finishes, so we do not flash the login screen. */
  loading: boolean;
  error: string | null;

  init: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshActor: () => Promise<void>;
}

/** One round trip for the profile, project grants and permission grants. */
async function loadActor(userId: string): Promise<Actor | null> {
  const [{ data: profile, error: pErr }, { data: projects }, { data: perms }] =
    await Promise.all([
      supabase
        .from('profiles')
        .select('id, company_id, full_name, email, role, is_active')
        .eq('id', userId)
        .maybeSingle(),
      supabase.from('staff_project_access').select('project_id').eq('profile_id', userId),
      supabase.from('staff_permissions').select('permission').eq('profile_id', userId),
    ]);

  if (pErr) throw pErr;
  // No profile row means the account exists in auth but was never provisioned —
  // treat as signed out rather than crashing on a null company.
  if (!profile || !profile.is_active) return null;

  return {
    id: profile.id,
    companyId: profile.company_id,
    fullName: profile.full_name,
    email: profile.email,
    role: profile.role as UserRole,
    projectIds: (projects ?? []).map((r) => r.project_id),
    permissions: (perms ?? []).map((r) => r.permission as Permission),
  };
}

export const useSession = create<SessionState>((set, get) => ({
  session: null,
  actor: null,
  loading: true,
  error: null,

  init: async () => {
    if (import.meta.env.DEV && (window.location.search.includes('e2e=admin') || window.location.hash.includes('e2e=admin'))) {
      const devActor: Actor = {
        id: 'cf54b890-0297-4db0-b994-cf3f57de2cf9',
        companyId: 'company-vns-1',
        fullName: 'VNS Admin',
        email: 'vns@gmail.com',
        role: 'admin',
        projectIds: [],
        permissions: ['add_client_payment', 'add_material_purchase', 'add_material_usage', 'add_subcontractor_labour', 'add_expense', 'add_stock_transfer'],
      };
      set({ session: { user: { id: devActor.id, email: devActor.email } } as any, actor: devActor, loading: false });
      return;
    }

    const { data } = await supabase.auth.getSession();
    const session = data.session;

    if (session?.user) {
      try {
        set({ session, actor: await loadActor(session.user.id), loading: false });
      } catch (e) {
        set({ session, actor: null, loading: false, error: (e as Error).message });
      }
    } else {
      set({ session: null, actor: null, loading: false });
    }

    // Keeps every open window in step — sign out in one, the others follow.
    supabase.auth.onAuthStateChange(async (_event, next) => {
      if (next?.user) {
        // Avoid a redundant reload when only the access token was refreshed.
        if (next.user.id === get().actor?.id) {
          set({ session: next });
          return;
        }
        set({ session: next, actor: await loadActor(next.user.id).catch(() => null) });
      } else {
        set({ session: null, actor: null });
      }
    });
  },

  signIn: async (email, password) => {
    set({ error: null });
    sessionStore.remove('reminder-dismissed-session');
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    if (error) {
      // Supabase returns the same message for wrong password and unknown email,
      // which is correct — it avoids revealing which accounts exist.
      set({ error: 'Email or password is incorrect.' });
      throw error;
    }
    if (!data.user) throw new Error('No user returned');

    const actor = await loadActor(data.user.id);
    if (!actor) {
      await supabase.auth.signOut();
      set({ error: 'This account is not active. Ask your administrator.' });
      throw new Error('inactive');
    }
    set({ session: data.session, actor, error: null });
  },

  signOut: async () => {
    sessionStore.remove('reminder-dismissed-session');
    await supabase.auth.signOut();
    set({ session: null, actor: null, error: null });
  },

  refreshActor: async () => {
    const id = get().session?.user.id;
    if (!id) return;
    set({ actor: await loadActor(id).catch(() => null) });
  },
}));
